import { Routes, Route, Navigate } from 'react-router';
import {
  BAD_REQUEST_ROUTE,
  BOOKINGS_MINE_ROUTE,
  HOME_ROUTE,
  HOME_LEGACY_ROUTE,
  LOGIN_ROUTE,
  NOT_AUTHORIZED_ROUTE,
  NOT_FOUND_ROUTE,
  PROFILE_ROUTE,
  RTSM_ROUTE,
  RTSM_ROUTE_USER_HASH,
  SCHEDULER_ROUTE,
  SEARCH_SPACE_ROUTE,
  USER_PLANNING_ROUTE,
  VERSION_ROUTE,
  ONBOARDING_ROUTE_START,
  ONBOARDING_ROUTE_WORK_HOURS,
  ONBOARDING_ROUTE_DESK,
  ONBOARDING_ROUTE_COLLEAGUES,
  BOOKINGS_VIEW_ROUTE,
  ONBOARDING_ROUTE_PROFILE_PICTURE,
  ONBOARDING_ROUTE_NOTIFICATIONS,
  ONBOARDING_ROUTE_FINALIZE,
  TOGETHER_ROUTE,
  RESET_PASSWORD_WITH_TOKEN_ROUTE,
  RESET_PASSWORD_WITH_CODE_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  useStore,
  AuthV4Service,
  AuthV3Service,
  RTSM_ROUTE_USER_V4,
  RTSM_ROUTE_USER_CODE_V4,
  GuardedRoute,
  SAML_ROUTE,
  BOOKINGS_UPDATE_ROUTE,
  RESOURCE_TAGS_ROUTE,
  queryClient,
  REAL_TIME,
  REAL_TIME_CODE,
  LOGIN_FIRST_ROUTE,
  LOGIN_PASSWORD_ROUTE,
  MANAGE_SPACES_ROUTE,
  ANALYTICS_ROUTE,
  ANALYTICS_OCCUPATION_ROUTE,
  ANALYTICS_BOOKING_ROUTE,
  ANALYTICS_ADOPTION_ROUTE,
  ANALYTICS_TEAM_ROUTE,
  ANALYTICS_HEATMAP_ROUTE,
} from '@jooxter/core';
import { MenuLayout } from '../layouts';
import { BackgroundLayout, ErrorEnum, JxtError, JxtLoginLayout } from '@jooxter/ui';
import { JSX, Suspense, useEffect, useState } from 'react';
import { OnboardingDoneGuard } from './OnboardingDoneGuard';
import { OnboardingNotDoneGuard } from './OnboardingNotDoneGuard';
import { usePermissions } from '@jooxter/permissions';
import { AnalyticReportAccessDTOReportEnum } from '@jooxter/api';
import { AppLoader } from './AppLoader';
import { AnalyticsAccessGate } from './AnalyticsAccessGate';
import { HomeRoute } from './HomeRoute';
import {
  UserContainer,
  Home,
  ForgotPassword,
  LoginPassword,
  LoginEmail,
  LoginFirst,
  ResetPassword,
  Saml,
  BookingView,
  Spaces,
  MyBookings,
  Colleagues,
  Version,
  RtsmGuardV3User,
  OnboardingStart,
  OnboardingWorkHours,
  OnboardingDesk,
  OnboardingColleagues,
  OnboardingProfilePicture,
  OnboardingNotifications,
  OnboardingFinalize,
  ManageMyResources,
  MeetUp,
  RtsmGuardV4User,
  RtsmGuardV3Code,
  RtsmGuardV4Code,
  BookingUpdate,
  ResourceTags,
  RealTimeGuardCode,
  RealTimeGuardUser,
  WithUserAnalyticsAccess,
} from './lazyPages';
import { getLandingRoute } from './helpers';

export const AppRoutes = (): JSX.Element => {
  const {
    permissions: { booking, home, collaborators, analytics, meetup, manageSpaces },
    isUserLoading,
    isAnalyticsLoading,
  } = usePermissions();
  const [token, setToken] = useStore((state) => [state.token, state.setToken]);
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      queryClient.removeQueries();
    }
  }, [token]);

  useEffect(() => {
    Promise.all([AuthV4Service.getInstance().getToken(), AuthV3Service.getInstance().getToken()]).then(([v4t, v3t]) => {
      if (v4t) {
        setToken(v4t);
      }
      if (v3t) {
        setToken(v3t);
      }
      setReady(true);
    });

    // run only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the user: the analytics access gates the analytics pages alone (AnalyticsAccessGate).
  if (!ready || isUserLoading) {
    return <AppLoader />;
  }

  // Pages are loaded on demand (lazyPages): the full-page loader while the first one loads (no
  // Suspense in the layouts: the navbar shows with the page); then the router's transitions keep the
  // current page on screen while the next one loads.
  return (
    <Suspense fallback={<AppLoader />}>
      <Routes>
        {/* Unguarded Routes */}
        <Route path={RESET_PASSWORD_WITH_TOKEN_ROUTE} element={<JxtLoginLayout />}>
          <Route path="" element={<ResetPassword />} />
        </Route>
        <Route path={RESET_PASSWORD_WITH_CODE_ROUTE} element={<JxtLoginLayout />}>
          <Route path="" element={<ResetPassword />} />
        </Route>
        <Route element={<BackgroundLayout className="justify-center items-center" />}>
          <Route path={VERSION_ROUTE} element={<Version />} />
          <Route path={RTSM_ROUTE_USER_HASH} element={<RtsmGuardV3Code />} />
          <Route path={RTSM_ROUTE_USER_CODE_V4} element={<RtsmGuardV4Code />} />
          <Route path={REAL_TIME_CODE} element={<RealTimeGuardCode />} />
          <Route path={NOT_FOUND_ROUTE} element={<JxtError code={ErrorEnum.NOT_FOUND} />} />
          <Route path={BAD_REQUEST_ROUTE} element={<JxtError code={ErrorEnum.BAD_REQUEST} />} />
          <Route path={NOT_AUTHORIZED_ROUTE} element={<JxtError code={ErrorEnum.NOT_AUTHORIZED} />} />
        </Route>
        {/* Non-Authenticated Routes: accessible only if user in not authenticated. Straight to the landing
            page: through /home, a user without booking would send a "Home" page view to GA (ND-6435). */}
        <Route element={<GuardedRoute isRouteAccessible={!token} redirectRoute={getLandingRoute(home)} />}>
          <Route path="/" element={<JxtLoginLayout />}>
            <Route path="" element={<LoginEmail />} />
            <Route path={LOGIN_PASSWORD_ROUTE} element={<LoginPassword />} />
            <Route path={LOGIN_FIRST_ROUTE} element={<LoginFirst />} />
            <Route path={FORGOT_PASSWORD_ROUTE} element={<ForgotPassword />} />
            <Route path={SAML_ROUTE} element={<Saml />} />
          </Route>
        </Route>
        {/* Authenticated Routes */}
        <Route element={<GuardedRoute isRouteAccessible={!!token} redirectRoute={LOGIN_ROUTE} />}>
          <Route element={<OnboardingDoneGuard />}>
            <Route element={<BackgroundLayout className="justify-center items-center" />}>
              <Route path={RTSM_ROUTE} element={<RtsmGuardV3User />} />
              <Route path={RTSM_ROUTE_USER_HASH} element={<RtsmGuardV3Code />} />
              <Route path={RTSM_ROUTE_USER_V4} element={<RtsmGuardV4User />} />
              <Route path={RTSM_ROUTE_USER_CODE_V4} element={<RtsmGuardV4Code />} />
              <Route path={REAL_TIME} element={<RealTimeGuardUser />} />
              <Route path={REAL_TIME_CODE} element={<RealTimeGuardCode />} />
            </Route>
            <Route element={<MenuLayout />}>
              <Route path={PROFILE_ROUTE} element={<UserContainer />} />
              {/* Routes for user with collaborators capability enabled */}
              <Route element={<GuardedRoute isRouteAccessible={collaborators} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={HOME_LEGACY_ROUTE} element={<Home />} />
                <Route path={USER_PLANNING_ROUTE} element={<Colleagues />} />
              </Route>
              <Route path={SEARCH_SPACE_ROUTE} element={<Spaces />} />
              <Route path={RESOURCE_TAGS_ROUTE} element={<ResourceTags />} />
              {/* Routes for user with booking capability enabled */}
              {/* Not for OmniVision users, who can't book (ND-6510). Presence and favorites only show with the
                  collaborators capability. */}
              <Route element={<GuardedRoute isRouteAccessible={home} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={HOME_ROUTE} element={<HomeRoute />} />
              </Route>
              <Route element={<GuardedRoute isRouteAccessible={booking} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={BOOKINGS_MINE_ROUTE} element={<MyBookings />} />
                <Route path={BOOKINGS_VIEW_ROUTE} element={<BookingView />} />
                <Route path={BOOKINGS_UPDATE_ROUTE} element={<BookingUpdate />} />
              </Route>
              <Route element={<GuardedRoute isRouteAccessible={meetup} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={TOGETHER_ROUTE} element={<MeetUp />} />
              </Route>
              <Route element={<AnalyticsAccessGate isLoading={isAnalyticsLoading} />}>
                <Route
                  element={
                    <GuardedRoute
                      isRouteAccessible={typeof analytics?.size === 'number' && analytics.size > 0}
                      redirectRoute={SEARCH_SPACE_ROUTE}
                    />
                  }
                >
                  {/* Routes for user with analytics capability enabled */}
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={!!analytics?.size && analytics.size > 0}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={analytics?.has(AnalyticReportAccessDTOReportEnum.Occupation)}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_OCCUPATION_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={analytics?.has(AnalyticReportAccessDTOReportEnum.Booking)}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_BOOKING_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={analytics?.has(AnalyticReportAccessDTOReportEnum.Adoption)}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_ADOPTION_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={analytics?.has(AnalyticReportAccessDTOReportEnum.Team)}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_TEAM_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                  <Route
                    element={
                      <GuardedRoute
                        isRouteAccessible={analytics?.has(AnalyticReportAccessDTOReportEnum.Heatmap)}
                        redirectRoute={SEARCH_SPACE_ROUTE}
                      />
                    }
                  >
                    <Route path={ANALYTICS_HEATMAP_ROUTE} element={<WithUserAnalyticsAccess />} />
                  </Route>
                </Route>
              </Route>
              {/* Route for space/floors/buildiing managers */}
              <Route element={<GuardedRoute isRouteAccessible={manageSpaces} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={MANAGE_SPACES_ROUTE} element={<ManageMyResources />} />
              </Route>
              {/* Support for legacy route */}
              <Route element={<GuardedRoute isRouteAccessible={false} redirectRoute={SEARCH_SPACE_ROUTE} />}>
                <Route path={SCHEDULER_ROUTE} element={<Spaces />} />
              </Route>
            </Route>
          </Route>
          <Route element={<OnboardingNotDoneGuard />}>
            <Route element={<BackgroundLayout />}>
              <Route path={ONBOARDING_ROUTE_START} element={<OnboardingStart />} />
              <Route path={ONBOARDING_ROUTE_WORK_HOURS} element={<OnboardingWorkHours />} />
              <Route path={ONBOARDING_ROUTE_DESK} element={<OnboardingDesk />} />
              <Route path={ONBOARDING_ROUTE_COLLEAGUES} element={<OnboardingColleagues />} />
              <Route path={ONBOARDING_ROUTE_PROFILE_PICTURE} element={<OnboardingProfilePicture />} />
              <Route path={ONBOARDING_ROUTE_NOTIFICATIONS} element={<OnboardingNotifications />} />
              <Route path={ONBOARDING_ROUTE_FINALIZE} element={<OnboardingFinalize />} />
            </Route>
          </Route>
        </Route>
        {/* Not found Route */}
        <Route path="*" element={<Navigate to={NOT_FOUND_ROUTE} replace />} />
      </Routes>
    </Suspense>
  );
};
